/** HTTP client. Every response is narrowed before it reaches a component. */

import { apiBaseUrl } from "./env";
import { heldCredential } from "./tabSession";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly detail: Record<string, string> = {},
  ) {
    super(message);
    this.name = "ApiError";
  }
}

interface ErrorBody {
  code?: string;
  message?: string;
  detail?: Record<string, string> | ValidationProblem[];
}

/** FastAPI's own refusal shape, which carries the field rather than a code. */
interface ValidationProblem {
  loc?: (string | number)[];
  msg?: string;
}

/** One line per refused field, so a schema refusal reads like every other one. */
function validationDetail(problems: ValidationProblem[]): Record<string, string> {
  return {
    refusals: problems
      .map((one) => {
        const where = (one.loc ?? []).filter((part) => part !== "body").join(".");
        return where === "" ? (one.msg ?? "") : `${where}: ${one.msg ?? ""}`;
      })
      .join(String.fromCharCode(10)),
  };
}

function asErrorBody(value: unknown): ErrorBody {
  return typeof value === "object" && value !== null
    ? (value as ErrorBody)
    : {};
}

/** Turn a fetch response into its parsed body, or throw the `ApiError` it refused with. */
async function toResult<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const body = asErrorBody(await response.json().catch(() => null));
    const detail = Array.isArray(body.detail)
      ? validationDetail(body.detail)
      : (body.detail ?? {});
    throw new ApiError(
      response.status,
      body.code ?? "unknown",
      body.message ?? (detail["refusals"] ?? response.statusText),
      detail,
    );
  }

  return response.status === 204
    ? (undefined as T)
    : ((await response.json()) as T);
}

export async function request<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  // A tab credential wins over the cookie, so a role picker makes this tab that person.
  const held = heldCredential();
  const response = await fetch(`${apiBaseUrl}${path}`, {
    credentials: "include",
    ...init,
    headers: {
      "content-type": "application/json",
      ...(held === undefined ? {} : { authorization: `Bearer ${held}` }),
      ...init.headers,
    },
  });
  return toResult<T>(response);
}

/** A multipart POST. No `content-type` here: the browser writes its own boundary. */
export async function requestForm<T>(path: string, form: FormData): Promise<T> {
  const held = heldCredential();
  const response = await fetch(`${apiBaseUrl}${path}`, {
    method: "POST",
    credentials: "include",
    headers: held === undefined ? {} : { authorization: `Bearer ${held}` },
    body: form,
  });
  return toResult<T>(response);
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "POST", body: JSON.stringify(body ?? {}) }),
  put: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "PUT", body: JSON.stringify(body) }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "PATCH", body: JSON.stringify(body) }),
  delete: <T>(path: string) => request<T>(path, { method: "DELETE" }),
  postForm: <T>(path: string, form: FormData) => requestForm<T>(path, form),
};

/** The api's one code for absent, unknown, revoked and expired sessions alike. */
export const UNAUTHENTICATED = "unauthenticated";

/** True when the api refused because nobody is signed in, rather than because of this request. */
export function isSignedOut(error: unknown): boolean {
  return error instanceof ApiError && error.code === UNAUTHENTICATED;
}
